/**
 * useWebRTC Hook
 * Maneja señalización y conexión WebRTC para sesiones GUI remotas.
 *
 * State management is delegated to `WebRTCSessionStore` (a plain class with
 * a subscribe/getSnapshot pattern). The hook consumes it with
 * `useSyncExternalStore` so React re-renders only when the relevant slice
 * of state changes. The remaining imperative code (PC lifecycle, data
 * channel, signaling, clipboard, ping/pong) is kept in this hook for
 * now; a future refactor could move it into a separate class as well.
 */

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { AdminWSManager } from '@/hooks/useWebSocket';
import { WebRTCSessionStore, setLatestInputStats } from './webRTCSessionStore';
import type {
  MultiUserSessionState,
  SessionRole,
  WebRTCMode,
  WebRTCSignalMessage,
} from '@/types/webrtc';

const randomId = () => Math.random().toString(36).slice(2, 10);

export const getIceServers = (): RTCIceServer[] => {
  // Preferimos variable global (inyectada por backend) y luego env.
  try {
    const globalServers = (window as unknown as { webrtcIceServers?: RTCIceServer[] })
      .webrtcIceServers;
    if (Array.isArray(globalServers) && globalServers.length > 0) return globalServers;
  } catch {}

  const envJson = import.meta.env.VITE_WEBRTC_ICE_SERVERS_JSON;
  if (envJson) {
    try {
      const parsed = JSON.parse(envJson);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {
      // ignore invalid json
    }
  }

  // ✅ Fallback por defecto: Google STUN para conexiones básicas
  // Para producción: configurar TURN servers vía VITE_WEBRTC_ICE_SERVERS_JSON
  // Ejemplo TURN: [{"urls":["turn:turn.example.com:3478"],"username":"user","credential":"pass"}]
  const defaultServers: RTCIceServer[] = [
    {
      urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'],
    },
  ];

  return defaultServers;
};

interface UseWebRTCOptions {
  mode?: WebRTCMode;
  displayIndex?: number;
  /**
   * List of display indices the picker selected for capture. The agent
   * spawns one WebRTC video track per index. `undefined` (or empty)
   * defaults to `[0]` for backward compatibility with frontends that
   * still pass the legacy single `displayIndex` field. The new
   * multi-monitor protocol (PR 2+) carries this list in the SDP offer.
   */
  displays?: number[];
}

export const useWebRTC = (agentId: string | null, options: UseWebRTCOptions = {}) => {
  // Single source of truth for all React-visible state. The store is created
  // lazily once per component instance and lives as long as the hook does.
  // useSyncExternalStore triggers a re-render whenever any field in the
  // snapshot changes, but only one snapshot reference per change → React
  // bails out via Object.is when nothing relevant changed.
  const storeRef = useRef<WebRTCSessionStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = new WebRTCSessionStore();
  }
  const store = storeRef.current;
  const sessionState_ = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const {
    status,
    error,
    authRequired,
    role,
    sessionState,
    cursorHidden,
    reconnectFailed,
    reconnectAttempt,
    inputError,
    inputSessionVersion,
    inputStats,
    // PR 6: live PC state. The store updates these from the
    // onconnectionstatechange / oniceconnectionstatechange / etc.
    // callbacks; the hook surfaces them in the return so the page
    // can render the WebRTCStateBadge.
    pcConnectionState,
    pcIceConnectionState,
    pcSignalingState,
    pcIceGatheringState,
    tracksReceived,
    lastTrackLabel,
    lastTrackKind,
  } = sessionState_;

  // Setters are stable bound methods of the store, so consumers can put
  // them in dependency arrays without re-renders. We alias them to
  // local names that match the original hook API to keep the diff
  // minimal — the rest of the hook can keep calling setStatus(...) etc.
  const setStatus = store.setStatus;
  const setError = store.setError;
  const setAuthRequired = store.setAuthRequired;
  const setRole = store.setRole;
  const setSessionState = store.setSessionState;
  const setCursorHidden = store.setCursorHidden;
  const setReconnectFailed = store.setReconnectFailed;
  const setReconnectAttempt = store.setReconnectAttempt;
  const setInputError = store.setInputError;
  // PR 6: live PC state mutators. The hook calls these from the
  // onconnectionstatechange / oniceconnectionstatechange / etc.
  // callbacks. The store updates trigger a re-render so the inline
  // WebRTCStateBadge on the page can show the current state without
  // DevTools.
  const setPcConnectionState = store.setPcConnectionState;
  const setPcIceConnectionState = store.setPcIceConnectionState;
  const setPcSignalingState = store.setPcSignalingState;
  const setPcIceGatheringState = store.setPcIceGatheringState;
  const noteTrackReceived = store.noteTrackReceived;
  const resetPcState = store.resetPcState;
  const dismissInputError = useCallback(() => store.setInputError(null), [store]);

  // PR 5: mirror inputStats to the module-level snapshot so the
  // global DebugPanel can show live counters without holding a
  // reference to this hook's store. Re-pushes on every change
  // because the store is the source of truth and the panel polls
  // it on a 1s cadence (see DebugPanel).
  useEffect(() => {
    setLatestInputStats(inputStats);
  }, [inputStats]);

  const connectPasswordRef = useRef<string | undefined>(undefined);
  const roleRef = useRef<SessionRole>('pending');
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const modeRef = useRef<WebRTCMode>(options.mode || 'gui');
  const displayIndexRef = useRef<number>(options.displayIndex ?? 0);
  // displaysRef mirrors what the picker passed in. We keep the legacy
  // displayIndexRef in sync so the public API (which still accepts a
  // single index for the common single-monitor case) keeps working,
  // but prefer the new list when non-empty.
  const displaysRef = useRef<number[]>(
    Array.isArray(options.displays) && options.displays.length > 0
      ? [...options.displays]
      : [options.displayIndex ?? 0]
  );
  const mountedRef = useRef(true);
  const userStoppedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const quickRetriesRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 10;
  const MAX_BACKOFF_MS = 30000;
  const JITTER_RATIO = 0.2;
  const diagnosticsRef = useRef<{ events: Array<{ ts: number; type: string; detail: any }> }>({
    events: [],
  });
  const connectionTimeoutRef = useRef<number | null>(null);
  const OFFER_TIMEOUT_MS = 15000; // Wait 15s for answer after sending offer

  // Data channel para input/control
  const dataChanRef = useRef<RTCDataChannel | null>(null);
  const dataChanReadyRef = useRef(false);
  // Invalidates queued/in-flight input whenever the underlying channel is
  // replaced or closed. Old gestures must never resume on a new channel.
  const inputGenerationRef = useRef(0);
  const pingTimerRef = useRef<number | null>(null);
  // Interactive input is intentionally never replayed after a channel
  // reconnect. Replaying an old mouse-up/down against a freshly-reset host
  // state can create clicks or leave a drag stuck. Kept as a ref only for
  // cleanup compatibility with older lifecycle code.
  const pendingInputsRef = useRef<Array<string>>([]);
  // Captured PC state at the moment cleanup() runs, used by the data channel
  // onclose dump so it still has meaningful values (pcRef.current is null by
  // the time the async onclose fires).
  const lastClosedPCStateRef = useRef<{
    connectionState: RTCPeerConnectionState | undefined;
    iceConnectionState: RTCIceConnectionState | undefined;
    signalingState: RTCSignalingState | undefined;
  } | null>(null);
  // Session-scoped diagnostic event ring. Reset on every startSession so the
  // dump for the current session doesn't include events from the previous one.
  // Holds up to MAX_DIAGNOSTIC_EVENTS events; older ones are dropped.
  const MAX_DIAGNOSTIC_EVENTS = 200;
  // WebRTC data channel buffering. Chromium/Safari drop sends when
  // bufferedAmount exceeds the per-stream threshold (~1MB on Chromium) and
  // the failure is silent — no error, no callback. When a user moves the
  // mouse quickly we can fire 100+ move events/sec; without backpressure
  // the buffer fills in <100ms and subsequent button events get lost.
  // We keep sends well under 256KB so there is headroom for retransmits
  // and so WebRTC's internal framing never blocks.
  const DATA_CHANNEL_BUFFER_HIGH_WATERMARK = 256 * 1024; // 256 KB
  // How long to wait between retries when the buffer is above the high
  // watermark. Short enough that a single dropped frame doesn't add visible
  // latency, long enough to let the consumer drain.
  const BUFFER_BACKOFF_MS = 8;
  // Per-session counters surfaced via the diagnostic dump so operators can
  // see whether input was dropped because the buffer was saturated.
  const sendDroppedBufferRef = useRef(0);
  const sendBackoffWaitsRef = useRef(0);
  // Ref para evitar referencias circulares en setupDataChannel
  const startSessionRef = useRef<
    ((mode: WebRTCMode, displayIndex: number) => Promise<void>) | null
  >(null);

  // Bug 4: input error banner is now sourced from the WebRTCSessionStore
  // (see the alias above). The store owns the dismiss behavior.

  // The admin WebSocket is shared by the whole application, so closing a
  // local PeerConnection does not make the backend's connection-level
  // cleanup run. Always release the server-side session before forgetting
  // its ID locally. Otherwise a failed offer/reconnect leaves a stale
  // participant (and potentially a stale controller) in the session state.
  const stopSignalingSession = useCallback(() => {
    const sessionId = sessionIdRef.current;
    if (!agentId || !sessionId) return;
    try {
      AdminWSManager.get().send?.({
        type: 'webrtc.stop',
        data: { agentId, sessionId },
      });
    } catch (_e) {
      // If the shared admin WebSocket is already gone, its server-side defer
      // performs the same cleanup when it observes the disconnect.
    }
  }, [agentId]);

  const cleanup = useCallback(() => {
    stopSignalingSession();
    try {
      // remove signaling message listener if registered
      const rm = (wsRef as any).removeMsgListener as (() => void) | undefined;
      if (rm) {
        try {
          rm();
        } catch {}
      }
    } catch {}
    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;

    try {
      dataChanRef.current?.close();
    } catch {}
    dataChanRef.current = null;
    dataChanReadyRef.current = false;
    inputGenerationRef.current++;
    pendingInputsRef.current.length = 0;
    try {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current as unknown as number);
        pingTimerRef.current = null;
      }
    } catch {}

    // Clear connection timeout
    try {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    } catch {}

    // Capture state BEFORE nulling the PC so the onclose dump still has
    // meaningful diagnostics. The data channel onclose fires asynchronously
    // (after pcRef.current?.close() schedules close events); by then the
    // refs below would already be null and the dump would show undefined
    // for every field, making diagnostics useless.
    let closedConnectionState: RTCPeerConnectionState | undefined;
    let closedIceConnectionState: RTCIceConnectionState | undefined;
    let closedSignalingState: RTCSignalingState | undefined;
    try {
      const pc = pcRef.current;
      if (pc) {
        closedConnectionState = pc.connectionState;
        closedIceConnectionState = pc.iceConnectionState;
        closedSignalingState = pc.signalingState;
        pc.close();
      }
    } catch {}
    pcRef.current = null;
    // Stash captured state so the onclose handler in setupDataChannel can
    // include it in the diagnostic dump.
    lastClosedPCStateRef.current = {
      connectionState: closedConnectionState,
      iceConnectionState: closedIceConnectionState,
      signalingState: closedSignalingState,
    };

    sessionIdRef.current = null;
    if (mountedRef.current) {
      setStatus('idle');
      setCursorHidden(false);
    }
    // clear any pending reconnect attempts
    try {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    } catch {}
  }, [setCursorHidden, setStatus, stopSignalingSession]);

  // Centralized reconnect scheduler.
  //
  // All reconnect paths (webrtc.stopped, webrtc.error, pc.onconnectionstatechange,
  // data-channel stall) funnel through here so we get a single pending timer
  // at a time and a single source of truth for attempt counts + jitter.
  //
  // Caller must already have decided that a reconnect is desired (e.g. user
  // did not explicitly stop). Pass `kind` to identify the trigger in logs.
  //
  // Behavior:
  //   - Cancels any previously queued reconnect timer before scheduling.
  //   - Increments `reconnectAttemptsRef` and applies ±20% jitter to delay.
  //   - Caps burst attempts at MAX_RECONNECT_ATTEMPTS. Past the cap, it sets
  //     `reconnectFailed` so the UI can show a "click to retry" banner
  //     instead of silently spinning.
  const scheduleReconnect = useCallback(
    (baseDelayMs: number, kind: string) => {
      try {
        if (userStoppedRef.current) return;
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          if (mountedRef.current) setReconnectFailed(true);
          console.warn(
            `[WebRTC] ⚠️ Reconnect cap (${MAX_RECONNECT_ATTEMPTS}) reached for "${kind}". Surfacing banner; waiting for user retry.`
          );
          return;
        }
        const jitter = 1 + (Math.random() * 2 - 1) * JITTER_RATIO; // [0.8, 1.2]
        const delay = Math.max(0, Math.round(baseDelayMs * jitter));
        reconnectAttemptsRef.current += 1;
        const attempt = reconnectAttemptsRef.current;
        if (mountedRef.current) {
          setStatus('reconnecting');
          setReconnectAttempt(attempt);
        }
        // Cancel any previously scheduled reconnect — only the latest survives.
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        console.info(
          `[WebRTC] 🔄 Reconnect scheduled in ${delay}ms (${kind}, attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}, base=${baseDelayMs}ms)`
        );
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          try {
            const fn = startSessionRef.current;
            if (fn) fn(modeRef.current, displayIndexRef.current);
          } catch (err) {
            console.error('[WebRTC] ❌ Reconnect startSession failed:', err);
          }
        }, delay) as unknown as number;
      } catch (err) {
        console.error('[WebRTC] ❌ scheduleReconnect error:', err);
      }
    },
    [setReconnectAttempt, setReconnectFailed, setStatus]
  );

  // (Legacy single-monitor attachStream is replaced below by the
  // multi-track version that routes by track label. See the
  // PR 3 block above for the rationale and the per-track <video>
  // ref map.)

  const stopSession = useCallback(() => {
    // mark as user-requested stop to avoid auto-reconnect
    userStoppedRef.current = true;
    reconnectAttemptsRef.current = 0;
    setReconnectAttempt(0);
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (mountedRef.current) setReconnectFailed(false);
    roleRef.current = 'pending';
    if (mountedRef.current) {
      setRole('pending');
      setSessionState(null);
      setError(null);
    }
    cleanup();
  }, [cleanup, setError, setReconnectAttempt, setRole, setSessionState, setReconnectFailed]);

  // Sends an input event over the WebRTC data channel.
  //
  // Returns a Promise that resolves when the message has been *accepted* by
  // the data channel (i.e. dc.send did not throw and the bufferedAmount is
  // below the high-watermark). Callers can `await` the result to serialize
  // dependent events — most importantly `move` before `button.down` so the
  // cursor lands on the pixel the user clicked (PR 2 will use this for
  // atomic clicks).
  //
  // Backpressure: if bufferedAmount is at/above the high-watermark we wait
  // BUFFER_BACKOFF_MS and retry. We cap retries at 50 (~400ms) to avoid
  // hanging the caller forever; after the cap we count it as a dropped
  // send and resolve so the next event in the chain (e.g. button.down)
  // still goes out — better to deliver a click at the wrong pixel than to
  // strand the user's mouse entirely.
  //
  // Observers cannot send input (agent drops it anyway, but skip the
  // round-trip). clipboard.get is always allowed.
  // PR 3: per-track video ref map. Keyed by the WebRTC track label
  // (e.g. "screen-0", "screen-1") which the agent matches to the
  // display it captured. The page registers each <video> element via
  // registerVideoRef() and we look it up in attachStream() to route
  // the right MediaStream to the right pane.
  //
  // The legacy `videoRef` (single) is kept as a compatibility handle
  // for tests and the single-monitor code path; in the multi-monitor
  // page the legacy ref is left null and only the map is used.
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  // registerVideoRef is called by each MonitorPane on mount. The hook
  // captures the element into the map so attachStream() can find it
  // by track label. Returns an unregister function that the pane
  // calls on unmount — important because a stale ref in the map would
  // keep a detached <video> alive and waste the browser's media
  // pipeline.
  const registerVideoRef = useCallback((label: string, el: HTMLVideoElement | null) => {
    if (el) {
      videoRefs.current.set(label, el);
    } else {
      videoRefs.current.delete(label);
    }
  }, []);

  const attachStream = useCallback(
    (event: RTCTrackEvent) => {
      const stream = event.streams?.[0] || new MediaStream([event.track]);
      // Pion exposes the agent's NewTrackLocalStaticSample ID as
      // MediaStreamTrack.id in the browser. Its human-facing label may still
      // be the generic "remote video", so it cannot be the primary routing
      // key when an agent sends more than one monitor.
      const trackID = event.track.id;
      const trackLabel = event.track.label;
      const streamIDs = (event.streams ?? []).map((stream) => stream.id);
      // Pion's stream ID is `screen-track-{trackPosition}-{displayIndex}`.
      // Chrome can expose the generic source label and an opaque track ID, but
      // keeps this media-stream identity intact across the SDP answer.
      const streamDisplayKeys = streamIDs.flatMap((streamID) => {
        const match = /^screen-track-\d+-(\d+)$/.exec(streamID);
        return match ? [`screen-${match[1]}`] : [];
      });
      const candidateKeys = [trackID, ...streamDisplayKeys, ...streamIDs, trackLabel];
      const routingKeys = candidateKeys.filter(
        (key): key is string => typeof key === 'string' && key.length > 0
      );
      const canonicalTrackKey = routingKeys.find((key) => /^screen-\d+$/.test(key));
      // PR 6: surface track reception on the inline state badge so the
      // operator can tell "agent never sent a track" from "agent sent a
      // track but it didn't land on any pane" (a binary mismatch shows
      // up as >0 tracks but with an unexpected label).
      noteTrackReceived(
        canonicalTrackKey ?? trackLabel ?? trackID ?? null,
        event.track.kind ?? null
      );
      // PR 6 fallback: the canonical "status → connected" transition
      // lives in pc.onconnectionstatechange + the webrtc.started
      // message handler. Both are reliable for post-PR 1 agents.
      // Pre-PR 1 agents don't send webrtc.started and may not always
      // surface a state='connected' onconnectionstatechange (older
      // pion versions / different ICE restart behavior). The act of
      // receiving a video track is a positive signal that the WebRTC
      // pipeline is live — if the PC reports 'connected' and our
      // React status is still 'connecting', promote it. Idempotent
      // (setStatus with the same value is a no-op via the store's
      // reference-equality bail-out).
      if (event.track.kind === 'video') {
        const pc = pcRef.current;
        if (pc?.connectionState === 'connected' && mountedRef.current) {
          const current = store.getStatus();
          if (current === 'connecting' || current === 'idle') {
            setStatus('connected');
            reconnectAttemptsRef.current = 0;
            setReconnectAttempt(0);
            quickRetriesRef.current = 0;
            if (mountedRef.current) setReconnectFailed(false);
          }
        }
      }
      // Route by canonical track ID first, then label for compatibility.
      // Older agents provide only one generic "remote video" track and fall
      // through to the first pane below; current multi-monitor agents expose
      // screen-N in track.id even when track.label is generic.
      let video: HTMLVideoElement | null | undefined = null;
      for (const key of routingKeys) {
        video = videoRefs.current.get(key);
        if (video) break;
      }
      if (!video && event.transceiver) {
        // Last protocol-level fallback: video transceivers were added in the
        // exact `selected` display order before creating the offer. Their MID
        // may include an SCTP m-line offset, so use their video-only position
        // rather than parsing the MID as a display index.
        const pc = pcRef.current;
        const videoTransceivers = pc
          ?.getTransceivers?.()
          .filter((transceiver) => transceiver.receiver.track.kind === 'video');
        const position = videoTransceivers?.findIndex(
          (transceiver) =>
            transceiver === event.transceiver || transceiver.mid === event.transceiver.mid
        );
        const displayIndex =
          position != null && position >= 0 ? displaysRef.current[position] : undefined;
        if (displayIndex != null) {
          video = videoRefs.current.get(`screen-${displayIndex}`);
        }
      }
      if (!video) {
        video = videoRef.current;
      }
      if (!video) {
        // Tier 3: first registered pane. Safe for the legacy
        // single-track path; the operator can still click around
        // to interact because input dispatch reads the per-pane
        // video element via the page's paneVideoRefs map.
        const first = videoRefs.current.values().next().value as HTMLVideoElement | undefined;
        video = first;
      }

      if (video) {
        video.srcObject = stream;
        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      }
      // noteTrackReceived is a stable bound method (see
      // webRTCSessionStore), so adding it here doesn't cause re-renders.
      // We include it because the lint rule expects the dependency
      // tracking to be exhaustive even when refs don't change.
    },
    [
      // setStatus / setReconnectFailed / store are stable bound
      // methods (class fields on the WebRTCSessionStore), so
      // including them in the dep array is a no-op for re-renders
      // but makes the lint exhaustive-deps rule happy.
      noteTrackReceived,
      setStatus,
      setReconnectAttempt,
      setReconnectFailed,
      store,
    ]
  );

  const sendInput = useCallback(
    async (input: Record<string, unknown>, expectedGeneration?: number): Promise<void> => {
      const generation = expectedGeneration ?? inputGenerationRef.current;
      if (generation !== inputGenerationRef.current) return;
      if (roleRef.current === 'observer' && input.type !== 'clipboard.get') {
        return;
      }
      // Stamp each event for diagnostics. The agent ignores this field.
      try {
        (input as Record<string, unknown>).ts = Date.now();
      } catch (_e) {}
      try {
        console.debug('[useWebRTC] sendInput payload=', input);
      } catch (_e) {}
      const payload = JSON.stringify(input);
      if (generation !== inputGenerationRef.current) return;
      if (!dataChanRef.current || !dataChanReadyRef.current) {
        // Never replay physical input across a disconnect. The host releases
        // held state on close, so a stale sequence has no safe meaning.
        console.warn('Data channel no listo — input descartado');
        return;
      }
      const dc = dataChanRef.current;
      // Backpressure loop: if the buffer is at/above the high-watermark,
      // wait briefly and retry. Each retry costs BUFFER_BACKOFF_MS; we cap
      // at 50 retries (~400ms) to bound caller latency.
      const MAX_BACKOFF_RETRIES = 50;
      for (let attempt = 0; attempt <= MAX_BACKOFF_RETRIES; attempt++) {
        try {
          if (generation !== inputGenerationRef.current) return;
          if (dc.readyState !== 'open' || !dataChanReadyRef.current) {
            // A new channel has a freshly-reset input state; do not replay.
            return;
          }
          const buffered = dc.bufferedAmount || 0;
          if (buffered >= DATA_CHANNEL_BUFFER_HIGH_WATERMARK) {
            // Motion is lossy by design: a later RAF move supersedes it.
            // Dropping it immediately prevents a long backlog from putting
            // button-up behind hundreds of obsolete positions.
            if (input.type === 'move') {
              sendDroppedBufferRef.current++;
              store.bumpInputDropped();
              return;
            }
            sendBackoffWaitsRef.current++;
            store.bumpInputBackoffWaits();
            await new Promise<void>((resolve) => setTimeout(resolve, BUFFER_BACKOFF_MS));
            if (generation !== inputGenerationRef.current) return;
            continue;
          }
          dc.send(payload);
          store.bumpInputSent();
          try {
            console.debug('[useWebRTC] sendInput: sending payload', payload);
          } catch (_e) {}
          return;
        } catch (err) {
          console.error('Error enviando input:', err);
          return;
        }
      }
      // Exhausted retries — count it as a dropped send and resolve so the
      // caller (e.g. the atomic click chain) can still proceed.
      sendDroppedBufferRef.current++;
      store.bumpInputDropped();
      // A button/key transition cannot be silently discarded: force channel
      // close so the host's OnClose reset releases any partial gesture.
      if (input.type !== 'move') {
        try {
          dc.close();
        } catch {}
      }
      console.warn(
        '[useWebRTC] sendInput: dropped after backpressure retries (bufferedAmount saturated)'
      );
      return;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Input serialization chain.
  //
  // Every input send — from the page (mouse, wheel) and from the touch
  // hook (touch tap, drag, long-press) — must go through this chain so
  // dc.send() is called in the order the user generated the events.
  // Without it, two sendInput calls in flight concurrently can wake
  // from backoff in microtask order (NOT call order), and a
  // `button.down` can end up in the channel ahead of the preceding
  // `move` — making the click land at the wrong pixel. The chain also
  // guarantees that a `button.up` cannot fire before its `button.down`
  // even if the user releases the mouse faster than the data channel
  // drains.
  //
  // Errors on one work item do NOT break the chain — each link is
  // followed by a `.then(() => {}, () => {})` that swallows the
  // previous result. The work item itself is responsible for logging
  // its own errors.
  const inputChainRef = useRef<Promise<void>>(Promise.resolve());
  const enqueueInput = useCallback((work: () => Promise<void>): Promise<void> => {
    const generation = inputGenerationRef.current;
    const guardedWork = () => {
      if (generation !== inputGenerationRef.current) return Promise.resolve();
      return work();
    };
    const next = inputChainRef.current.then(guardedWork, guardedWork);
    inputChainRef.current = next.then(
      () => {},
      () => {}
    );
    return next;
  }, []);
  // Reset the chain whenever the data channel closes so a stale chain
  // from a previous session cannot process events from a new one.
  //
  // The hook is mounted per session, so capturing `dataChanRef.current`
  // once on mount is safe — the channel that is live at that point is
  // the one we want to listen on. We intentionally do NOT depend on the
  // ref (mutating refs does not re-render and would cause this effect
  // to never re-fire).
  useEffect(() => {
    const dc = dataChanRef.current;
    if (!dc) return;
    const onClose = () => {
      inputChainRef.current = Promise.resolve();
    };
    dc.addEventListener?.('close', onClose);
    return () => {
      dc.removeEventListener?.('close', onClose);
    };
  }, []);

  // Clipboard sync state
  const clipboardSyncTimerRef = useRef<number | null>(null);
  const clipboardSyncEnabledRef = useRef(false);
  const lastLocalClipboardRef = useRef('');
  const lastRemoteClipboardRef = useRef('');
  const clipboardReqSeqRef = useRef(0);
  const clipboardWaitersRef = useRef(
    new Map<string, { resolve: (v: string) => void; reject: (e: any) => void }>()
  );

  const canUseClipboardAPI = useCallback(() => {
    try {
      return !!(
        navigator &&
        (navigator as any).clipboard &&
        (navigator as any).clipboard.readText &&
        (navigator as any).clipboard.writeText
      );
    } catch (_e) {
      return false;
    }
  }, []);

  const readLocalClipboard = useCallback(async () => {
    if (!canUseClipboardAPI()) throw new Error('Clipboard API no disponible');
    return await (navigator as any).clipboard.readText();
  }, [canUseClipboardAPI]);

  const writeLocalClipboard = useCallback(
    async (text: string) => {
      if (!canUseClipboardAPI()) throw new Error('Clipboard API no disponible');
      await (navigator as any).clipboard.writeText(text || '');
    },
    [canUseClipboardAPI]
  );

  const requestRemoteClipboard = useCallback((): Promise<string> => {
    if (!dataChanRef.current || !dataChanReadyRef.current)
      return Promise.reject(new Error('Canal de input no disponible'));
    const reqId = 'cb_' + ++clipboardReqSeqRef.current + '_' + Date.now();
    const p = new Promise<string>((resolve, reject) => {
      try {
        clipboardWaitersRef.current.set(reqId, { resolve, reject });
        window.setTimeout(() => {
          if (clipboardWaitersRef.current.has(reqId)) {
            clipboardWaitersRef.current.delete(reqId);
            reject(new Error('Timeout de portapapeles'));
          }
        }, 3000);
        sendInput({ type: 'clipboard.get', requestId: reqId });
      } catch (_e) {
        reject(_e);
      }
    });
    return p;
  }, [sendInput]);

  const pushClipboardToAgent = useCallback(async () => {
    try {
      const text = await readLocalClipboard();
      if (!text || text === lastLocalClipboardRef.current) return;
      lastLocalClipboardRef.current = text;
      sendInput({ type: 'clipboard.set', text });
    } catch (_e) {
      console.warn('No se pudo leer el portapapeles local', _e);
    }
  }, [readLocalClipboard, sendInput]);

  const pullClipboardFromAgent = useCallback(async () => {
    try {
      const text = await requestRemoteClipboard();
      if (typeof text !== 'string') return;
      lastRemoteClipboardRef.current = text;
      await writeLocalClipboard(text);
      lastLocalClipboardRef.current = text;
    } catch (_e) {
      console.warn('No se pudo traer el portapapeles remoto', _e);
    }
  }, [requestRemoteClipboard, writeLocalClipboard]);

  const pushClipboardFilesToAgent = useCallback(async (): Promise<string[]> => {
    try {
      // Clipboard API not fully supported - just return empty for now
      return [];
    } catch (_e) {
      console.warn('pushClipboardFilesToAgent failed', _e);
      return [];
    }
  }, []);

  const startClipboardSync = useCallback(() => {
    if (clipboardSyncTimerRef.current) return;
    if (!canUseClipboardAPI()) {
      console.warn('Clipboard API no disponible');
      return;
    }
    clipboardSyncEnabledRef.current = true;
    try {
      clipboardSyncTimerRef.current = window.setInterval(async () => {
        if (!clipboardSyncEnabledRef.current || !dataChanRef.current || !dataChanReadyRef.current)
          return;
        try {
          const local = await readLocalClipboard();
          if (local && local !== lastLocalClipboardRef.current) {
            lastLocalClipboardRef.current = local;
            sendInput({ type: 'clipboard.set', text: local });
          }
        } catch {}

        // Try file/image clipboard items (if supported)
        try {
          if ((navigator.clipboard as any) && (navigator.clipboard as any).read) {
            try {
              await pushClipboardFilesToAgent();
            } catch {}
          }
        } catch {}

        try {
          const remote = await requestRemoteClipboard();
          if (remote && remote !== lastRemoteClipboardRef.current) {
            lastRemoteClipboardRef.current = remote;
            if (remote !== lastLocalClipboardRef.current) {
              try {
                await writeLocalClipboard(remote);
                lastLocalClipboardRef.current = remote;
              } catch {}
            }
          }
        } catch {}
      }, 2000) as unknown as number;
    } catch (_e) {
      console.warn('Failed starting clipboard sync', _e);
    }
  }, [
    canUseClipboardAPI,
    readLocalClipboard,
    requestRemoteClipboard,
    writeLocalClipboard,
    sendInput,
    pushClipboardFilesToAgent,
  ]);

  const stopClipboardSync = useCallback(() => {
    clipboardSyncEnabledRef.current = false;
    try {
      if (clipboardSyncTimerRef.current) {
        clearInterval(clipboardSyncTimerRef.current as unknown as number);
        clipboardSyncTimerRef.current = null;
      }
    } catch {}
  }, []);

  const handleSignalMessage = useCallback(
    async (message: WebRTCSignalMessage) => {
      console.log('[WebRTC] 📨 Mensaje de señalización recibido:', message.type);
      if (!pcRef.current) return;
      switch (message.type) {
        case 'webrtc.answer': {
          const sdp = (message as any)?.data?.sdp;
          if (sdp && pcRef.current) {
            console.log('[WebRTC] ✅ RESPUESTA del agente recibida - limpiando timeout');
            // Clear the offer timeout since we got a response
            if (connectionTimeoutRef.current) {
              clearTimeout(connectionTimeoutRef.current);
              connectionTimeoutRef.current = null;
            }

            const pc = pcRef.current;
            if (pc.signalingState === 'stable' && pc.currentRemoteDescription?.type === 'answer') {
              console.warn('[WebRTC] ⚠️ Answer duplicada ignorada (ya en stable)');
              break;
            }

            try {
              await pc.setRemoteDescription({ type: 'answer', sdp });
              console.log('[WebRTC] ✅ Remote description configurado');
            } catch (err) {
              console.warn('[WebRTC] ⚠️ Error configurando remote description:', err);
            }
          }
          break;
        }
        case 'webrtc.ice': {
          const candidate = (message as any)?.data?.candidate;
          if (candidate) {
            try {
              await pcRef.current.addIceCandidate(candidate);
              console.log('[WebRTC] 🧊 ICE candidate agregado');
            } catch (err) {
              console.warn('[WebRTC] ⚠️ Error agregando ICE candidate:', err);
            }
          }
          break;
        }
        case 'webrtc.started': {
          console.log('[WebRTC] ✅ Sesión iniciada por el agente');
          if (mountedRef.current) setStatus('connected');
          break;
        }
        case 'webrtc.warning': {
          const msg = (message as any)?.data?.message;
          const graceSeconds = (message as any)?.data?.graceSeconds || 10;
          if (msg === 'connection_unstable') {
            console.warn(
              `[WebRTC] ⚠️ Conexión inestable detectada por agente, intentando recuperar (${graceSeconds}s grace)...`
            );

            if (mountedRef.current) {
              setError(`Conexión inestable, reconectando en ${graceSeconds}s...`);
            }

            // ✅ Trigger ICE restart proactivo para recuperar conexión
            try {
              const pc = pcRef.current;
              if (
                pc &&
                (pc.connectionState === 'connected' || pc.connectionState === 'connecting')
              ) {
                console.log('[WebRTC] 🔄 Triggering ICE restart para recuperar conexión');
                pc.restartIce();
              }
            } catch (_e) {
              console.warn('[WebRTC] ICE restart fallido:', _e);
            }
          }
          break;
        }
        case 'webrtc.recovered': {
          console.log('[WebRTC] ✅ Conexión recuperada por agente');
          if (mountedRef.current) {
            setError(null);
            setStatus('connected');
            setReconnectFailed(false);
          }
          // ✅ Reset retry counters tras recuperación exitosa
          quickRetriesRef.current = 0;
          reconnectAttemptsRef.current = 0;
          setReconnectAttempt(0);
          break;
        }
        case 'webrtc.stopped': {
          // Agent requested stop or session ended. If user did not request stop,
          // treat as recoverable: log detailed diagnostic info and schedule
          // an immediate reconnect attempt (short quick-retries), then fall back
          // to exponential backoff.
          const reason = (message as any)?.data?.reason || '';

          if (userStoppedRef.current) {
            // user explicitly stopped -> honor it
            stopSession();
            break;
          }

          // agent_disconnected is special: the agent's control-plane WS
          // dropped, and the agent is in the middle of reconnecting to
          // the backend (see backend broadcast `webrtc.stopped` with
          // reason=agent_disconnected, which the backend also fans out
          // as terminal.stopped). The next offer from us will hit
          // `agent_offline` for ~1-3s while the agent reconnects, so
          // we (a) wait at least 3s before re-offering, and (b) show
          // a friendlier message than the generic "Agente no
          // responde" timeout error.
          const isAgentReconnect = reason === 'agent_disconnected';

          // Cancel the offer-answer timeout up front: cleanup() (called below)
          // would clear it, but the timeout's body also runs setError/setStatus
          // ('Agente no responde') that would race with the reconnect
          // scheduling. Clearing it first ensures the user only sees the
          // stopped → reconnecting flow, not a stale timeout error.
          try {
            if (connectionTimeoutRef.current) {
              clearTimeout(connectionTimeoutRef.current);
              connectionTimeoutRef.current = null;
            }
          } catch {}

          // Determine retry strategy and update counters BEFORE logging so
          // the log shows the updated `quickRetriesRef`.
          //
          // For `agent_disconnected`, the agent is already in the middle
          // of its own reconnect loop (TCP → backend → new agent.hello),
          // typically 1-3s. A 1s quick-retry from us would race the
          // agent's reconnect and we'd just hit `agent_offline` again,
          // burning the quick-retry budget. So we floor the delay at
          // 3s for that reason and surface a friendly "el agente se
          // está reconectando" message instead of letting the next
          // offer fail with the generic "Agente no responde" error.
          const AGENT_RECONNECT_FLOOR_MS = 3000;
          let delay: number;
          if ((quickRetriesRef.current || 0) < 2) {
            quickRetriesRef.current = (quickRetriesRef.current || 0) + 1;
            delay = 1000 * quickRetriesRef.current; // 1s, then 2s
            if (isAgentReconnect) {
              delay = Math.max(delay, AGENT_RECONNECT_FLOOR_MS);
            }
          } else {
            const attempts = reconnectAttemptsRef.current || 0;
            delay = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
            // Reset quick retries after we start exponential backoff
            quickRetriesRef.current = 0;
          }

          if (isAgentReconnect && mountedRef.current) {
            // Friendlier UI state while we wait for the agent to come
            // back. Don't set status to 'error' — that's reserved for
            // genuine failures. We use 'connecting' (the closest state
            // the store has; the next startSession will move it to
            // 'connected' on success or 'error' on real failure).
            try {
              setError('El agente se está reconectando…');
            } catch (_e) {
              /* setError is required, ignore */
            }
          }

          // Build diagnostic dump and send to server to aid correlation with agent logs
          console.warn('[WebRTC] ⚠️ Sesión detenida por el agente:', reason);
          try {
            const pc = pcRef.current;
            const dump = {
              sessionId: sessionIdRef.current,
              reason,
              ts: Date.now(),
              pcConnectionState: pc?.connectionState,
              pcIceConnectionState: pc?.iceConnectionState,
              pcSignalingState: pc?.signalingState,
              dataChannelReady: dataChanReadyRef.current,
              reconnectAttempts: reconnectAttemptsRef.current,
              quickRetries: quickRetriesRef.current,
              recentEvents: diagnosticsRef.current.events.slice(-100),
            };
            // Log diagnostic dump
            console.warn('[WebRTC] 📊 Diagnostic dump:', JSON.stringify(dump, null, 2));
            // Send diagnostics to server-side admin channel when available
            try {
              AdminWSManager.get().send?.({ type: 'webrtc.client_diagnostics', data: dump });
            } catch (_e) {
              console.warn('[WebRTC] ⚠️ No se pudo enviar dump al servidor:', _e);
            }
          } catch (_e) {
            console.warn('[WebRTC] ⚠️ Error armando dump:', _e);
          }

          // Clean up the old PeerConnection before scheduling reconnect to avoid
          // stale connections interfering with inputs/data channel
          try {
            cleanup();
          } catch (_e) {
            console.warn('[WebRTC] ⚠️ Error durante cleanup antes de reconectar:', _e);
          }

          // Funnel through centralized scheduler: it cancels any pending timer,
          // increments attempts atomically, applies jitter, and respects the
          // burst cap.
          scheduleReconnect(delay, 'stopped');
          break;
        }
        case 'webrtc.error': {
          const msgData = (message as any)?.data || {};
          const msgSessionId = msgData?.sessionId as string | undefined;
          const mySessionId = sessionIdRef.current;
          if (msgSessionId && mySessionId && msgSessionId !== mySessionId) {
            break;
          }

          // The backend created this session before the agent could report a
          // signaling error (for example an auth rejection). Release it now:
          // an auth failure does not always schedule a reconnect, and leaving
          // it registered would make the next visit appear as another viewer.
          // Do not run full cleanup here: recoverable errors must keep this
          // message listener alive to receive webrtc.recovered.
          stopSignalingSession();

          const msg = msgData?.message || 'Error en sesión WebRTC';
          // Gate de contraseña: el agente rechazó por credencial. No reintentar
          // en loop — pedirle la contraseña al usuario.
          if (msgData?.code === 'auth_failed') {
            connectPasswordRef.current = undefined;
            if (mountedRef.current) {
              setAuthRequired(true);
              setError('Contraseña requerida o incorrecta');
              setStatus('error');
            }
            console.warn('useWebRTC: agente requiere contraseña');
            break;
          }
          if (mountedRef.current) {
            setError(msg);
            setStatus('error');
          }
          console.error('useWebRTC: signaling error from agent:', msg);
          // schedule reconnect unless user explicitly stopped
          if (!userStoppedRef.current) {
            const attempts = reconnectAttemptsRef.current || 0;
            const delay = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
            scheduleReconnect(delay, 'error');
          }
          break;
        }
        default:
          break;
      }
    },
    // cleanup and startSession are declared later; use refs to avoid circular deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stopSession, scheduleReconnect]
  );

  const setupDataChannel = useCallback(
    (channel: RTCDataChannel) => {
      const channelGeneration = ++inputGenerationRef.current;
      dataChanRef.current = channel;
      dataChanReadyRef.current = false;
      try {
        console.log(
          '[DataChannel] setupDataChannel called, label=',
          channel.label,
          'readyState=',
          channel.readyState
        );
      } catch (_e) {}
      let lastPongTs = Date.now();
      let missedPongs = 0;
      const MAX_MISSED_PONGS = 3;
      const PING_INTERVAL_MS = 3000;
      const PONG_TIMEOUT_MS = 5000;

      channel.onopen = () => {
        if (channelGeneration !== inputGenerationRef.current || dataChanRef.current !== channel)
          return;
        dataChanReadyRef.current = true;
        lastPongTs = Date.now();
        missedPongs = 0;
        console.log('[DataChannel] ✅ Abierto y listo para inputs');

        // ✅ Ping/Pong con detección de timeout
        try {
          if (!pingTimerRef.current) {
            pingTimerRef.current = window.setInterval(() => {
              // Only consider the channel "alive" if the data channel object
              // is still attached AND its readyState is 'open'. The readyState
              // check guards against the window where the channel transitioned
              // to closing/closed but onclose hasn't fired yet (and the
              // dataChanReadyRef flag hasn't been cleared), or where the agent
              // never opened the input channel at all.
              const dc = dataChanRef.current;
              if (!dc || dc.readyState !== 'open' || !dataChanReadyRef.current) return;

              const now = Date.now();
              const timeSinceLastPong = now - lastPongTs;

              // Verificar si pasó demasiado tiempo sin pong
              if (timeSinceLastPong > PONG_TIMEOUT_MS) {
                missedPongs++;
                console.warn(
                  `[DataChannel] ⚠️ Missed pong #${missedPongs} (${timeSinceLastPong}ms desde último pong)`
                );

                if (missedPongs >= MAX_MISSED_PONGS) {
                  console.error(
                    '[DataChannel] ❌ Canal muerto detectado (3 pongs perdidos), forzando reconexión'
                  );
                  // ✅ Forzar reconexión inmediata usando ref
                  try {
                    cleanup();
                    if (startSessionRef.current) {
                      startSessionRef.current(modeRef.current, displayIndexRef.current);
                    }
                  } catch (_e) {
                    console.error('[DataChannel] Error en reconexión forzada:', _e);
                  }
                  return;
                }
              }

              // Enviar ping
              try {
                const pingMsg = JSON.stringify({ type: 'ping', ts: now });
                dc.send(pingMsg);
              } catch (_e) {
                console.error('[DataChannel] Error enviando ping:', _e);
                missedPongs++;
              }
            }, PING_INTERVAL_MS) as unknown as number;
          }
        } catch (_e) {
          console.warn('[DataChannel] Error configurando ping timer:', _e);
        }

        // PR 5: one-shot self-test. We send a `selftest` message
        // immediately on channel open and measure the round-trip
        // time to the `selftest.echo` reply. This gives us a real
        // data-channel health signal in the first second instead of
        // waiting for the periodic 3s keepalive ping. A degraded RTT
        // (>200ms) is logged and surfaced in the DebugPanel so the
        // operator sees the data channel is sick before the user
        // complains about missed clicks.
        try {
          const selftestTs = Date.now();
          const selftestMsg = JSON.stringify({ type: 'selftest', ts: selftestTs });
          channel.send(selftestMsg);
          store.bumpInputSent();
        } catch (err) {
          console.warn('[DataChannel] Error enviando self-test:', err);
        }

        // A reconnection starts with a clean remote input state. Discard any
        // legacy queue residue instead of replaying physical events.
        pendingInputsRef.current.length = 0;

        // NOTE: Do not start automatic clipboard polling here. Manual sync
        // is available via the UI button to avoid continuous polling.
      };

      channel.onclose = () => {
        // A late close from a superseded channel must not invalidate its
        // replacement or clear its state.
        if (channelGeneration !== inputGenerationRef.current || dataChanRef.current !== channel)
          return;
        inputGenerationRef.current++;
        dataChanReadyRef.current = false;
        pendingInputsRef.current.length = 0;
        // Ensure we don't keep a stale reference to a closed channel
        try {
          dataChanRef.current = null;
        } catch (_e) {}
        try {
          // Use the captured PC state from cleanup() (saved in
          // lastClosedPCStateRef) instead of reading pcRef.current. By the
          // time the async onclose fires, pcRef.current is already null
          // and every state field would show undefined. The captured
          // snapshot reflects the state the PC had at the moment it was
          // torn down.
          const closedState = lastClosedPCStateRef.current;
          const dump = {
            sessionId: sessionIdRef.current,
            agentId,
            ts: Date.now(),
            pcConnectionState: closedState?.connectionState,
            pcIceConnectionState: closedState?.iceConnectionState,
            pcSignalingState: closedState?.signalingState,
            dataChannelReady: dataChanReadyRef.current,
            recentEvents: diagnosticsRef.current.events.slice(-50),
            stack: new Error().stack,
          };
          console.warn('[DataChannel] ⚠️ Canal cerrado', dump);
          try {
            AdminWSManager.get().send?.({ type: 'webrtc.client_diagnostics', data: dump });
          } catch (_e) {
            console.warn('[DataChannel] ⚠️ No se pudo enviar dump al servidor:', _e);
          }
        } catch (_e) {
          console.warn('[DataChannel] ⚠️ Canal cerrado (error armando dump):', _e);
        }
        try {
          if (pingTimerRef.current) {
            clearInterval(pingTimerRef.current as unknown as number);
            pingTimerRef.current = null;
          }
        } catch {}
        try {
          stopClipboardSync();
        } catch (_e) {}
      };

      channel.onerror = (event) => {
        console.error('[DataChannel] ❌ Error:', event);
        dataChanReadyRef.current = false;
      };

      channel.onmessage = (event) => {
        // Mensajes del servidor (ej: pong, clipboard, confirmaciones)
        try {
          const raw = event.data;
          let msg: any = null;
          if (typeof raw === 'string') {
            msg = JSON.parse(raw);
          } else if (raw instanceof ArrayBuffer) {
            msg = JSON.parse(new TextDecoder().decode(new Uint8Array(raw)));
          } else if ((raw as any).data) {
            msg =
              typeof (raw as any).data === 'string'
                ? JSON.parse((raw as any).data)
                : JSON.parse(new TextDecoder().decode(new Uint8Array((raw as any).data)));
          }
          if (!msg || !msg.type) return;

          // ✅ Handle pong response
          if (msg.type === 'pong') {
            lastPongTs = Date.now();
            missedPongs = 0;
            return;
          }

          // cursor state feedback from agent
          if (msg.type === 'cursor.state') {
            const hidden = msg.hidden === true;
            setCursorHidden(hidden);
            return;
          }

          // PR 3: input session version. Sent by the agent every time
          // the input data channel opens (initial connect AND every
          // reconnection). The frontend uses this as a dependency in
          // effects that need to re-sync state after a reset — most
          // importantly the modifier state, which the agent releases
          // on channel close via ResetInputStateDispatch. Without the
          // bump, a modifier held by the user during a brief
          // disconnect would not be re-applied on the agent side and
          // the next key event would arrive without the modifier
          // context.
          if (msg.type === 'input.session') {
            const v = typeof msg.version === 'number' ? msg.version : 0;
            if (v > 0 && v !== store.getInputSessionVersion()) {
              console.log('[DataChannel] input session version=', v);
              store.setInputSessionVersion(v);
            }
            return;
          }

          // PR 5: self-test echo. The agent mirrors back the `ts` we
          // sent in the `selftest` message. Round-trip time is
          // computed locally and stored on the inputStats so the
          // DebugPanel can surface a degraded data channel before any
          // user click fails. Healthy <50ms, degraded 50-200ms,
          // broken >200ms.
          if (msg.type === 'selftest.echo') {
            const sentAt = typeof msg.ts === 'number' ? msg.ts : 0;
            if (sentAt > 0) {
              const rtt = Date.now() - sentAt;
              store.setInputPingRtt(rtt, Date.now());
              if (rtt > 200) {
                console.warn(`[DataChannel] selftest RTT ${rtt}ms — degraded`);
              } else {
                console.log(`[DataChannel] selftest RTT ${rtt}ms`);
              }
            }
            return;
          }

          // Bug 4: input error feedback from agent. Surfaces a banner in
          // the page so the user sees "input is not reaching the host"
          // instead of wondering why their clicks don't work. Sampled
          // errors come every 50 events or 60s (whichever first), so
          // a persistent problem stays visible without spam.
          if (msg.type === 'input.error') {
            const action = typeof msg.action === 'string' ? msg.action : 'input';
            const message = typeof msg.message === 'string' ? msg.message : 'Error de input';
            const count = typeof msg.count === 'number' ? msg.count : 1;
            const sampled = msg.sampled === true;
            setInputError({ action, message, count, sampled, at: Date.now() });
            return;
          }

          // clipboard.data handling
          if (msg.type === 'clipboard.data') {
            const reqId = msg.requestId || '';
            const text = typeof msg.text === 'string' ? msg.text : '';
            const err = msg.error ? String(msg.error) : '';
            if (reqId && clipboardWaitersRef.current.has(reqId)) {
              const w = clipboardWaitersRef.current.get(reqId)!;
              clipboardWaitersRef.current.delete(reqId);
              if (err) w.reject(new Error(err));
              else w.resolve(text);
              return;
            }
            if (!clipboardSyncEnabledRef.current) return;
            if (err) return;
            if (text === lastRemoteClipboardRef.current) return;
            lastRemoteClipboardRef.current = text;
            if (text && text !== lastLocalClipboardRef.current) {
              try {
                writeLocalClipboard(text)
                  .then(() => {
                    lastLocalClipboardRef.current = text;
                  })
                  .catch(() => {});
              } catch {}
            }
            return;
          }

          // other message types currently logged
          console.log('[DataChannel] 📨 Mensaje:', msg);
        } catch (_e) {
          // ignore parse errors
        }
      };
    },
    [
      agentId,
      cleanup,
      stopClipboardSync,
      writeLocalClipboard,
      setCursorHidden,
      setInputError,
      store,
    ]
  );

  const startSession = useCallback(
    async (mode: WebRTCMode = 'gui', displayIndex = 0) => {
      if (!agentId) {
        setError('Agente inválido');
        setStatus('error');
        console.error('[WebRTC] ❌ Agente ID inválido');
        return;
      }

      // Fresh session: reset the diagnostic event ring so dumps for THIS
      // session only include events generated during THIS session's lifetime.
      // Without this, dumps pick up events from the previous session that
      // happened to share the same hook instance.
      diagnosticsRef.current.events.length = 0;

      // Fresh user/system-initiated start: clear the user-stopped latch so
      // auto-reconnect (scheduleReconnect / pc.onconnectionstatechange) is
      // allowed to run if this attempt fails. Without this, once any code
      // path called stopSession(), the latch stayed `true` forever and
      // silently disabled all future reconnects.
      userStoppedRef.current = false;

      // Reiniciar estado previo
      console.log('[WebRTC] 🔄 Iniciando nueva sesión WebRTC para agente:', agentId);
      // cleanup() signals any preceding session before it clears sessionIdRef.
      cleanup();
      modeRef.current = mode;
      displayIndexRef.current = displayIndex;
      const selected =
        displaysRef.current[0] === displayIndex ? [...displaysRef.current] : [displayIndex];
      displaysRef.current = selected;
      setStatus('connecting');
      setError(null);
      // PR 6: clear the live PC state + track counter so a stale
      // "tracks=2" from a previous session doesn't leak into the
      // new session's inline badge.
      resetPcState();

      const iceServers = getIceServers();
      console.log(
        '[WebRTC] 🧊 ICE Servers configurados:',
        iceServers.length > 0 ? iceServers : 'NINGUNO (solo LAN)'
      );
      try {
        // expose effective ice servers globally for debug UI
        (window as any).webrtcIceServers = iceServers;
      } catch {}
      const PeerCtor = (window as any).RTCPeerConnection as typeof RTCPeerConnection | undefined;
      if (!PeerCtor) {
        setError('WebRTC no soportado en este navegador');
        setStatus('error');
        console.error('[WebRTC] ❌ RTCPeerConnection no disponible');
        return;
      }

      const pc = new PeerCtor({ iceServers });
      pcRef.current = pc;
      const signalingSessionId = randomId();
      sessionIdRef.current = signalingSessionId;
      let offerSent = false;
      const earlyCandidates: RTCIceCandidate[] = [];
      const sendCandidate = (candidate: RTCIceCandidate) => {
        if (pcRef.current !== pc || sessionIdRef.current !== signalingSessionId) return;
        AdminWSManager.get().send?.({
          type: 'webrtc.ice',
          data: { agentId, sessionId: signalingSessionId, candidate },
        });
      };
      console.log('[WebRTC] ✅ PeerConnection creado');

      pc.ontrack = (event) => {
        attachStream(event);
      };

      // Extra diagnostic logging to help debug disconnects
      pc.oniceconnectionstatechange = () => {
        console.log('[WebRTC] 🧊 ICE State:', pc.iceConnectionState);
        setPcIceConnectionState(pc.iceConnectionState);
        try {
          const a = diagnosticsRef.current.events;
          a.push({ ts: Date.now(), type: 'iceConnectionState', detail: pc.iceConnectionState });
          if (a.length > MAX_DIAGNOSTIC_EVENTS) a.splice(0, a.length - MAX_DIAGNOSTIC_EVENTS);
        } catch (_e) {}
      };
      pc.onsignalingstatechange = () => {
        console.log('[WebRTC] 📡 Signaling State:', pc.signalingState);
        setPcSignalingState(pc.signalingState);
        try {
          const a = diagnosticsRef.current.events;
          a.push({ ts: Date.now(), type: 'signalingState', detail: pc.signalingState });
          if (a.length > MAX_DIAGNOSTIC_EVENTS) a.splice(0, a.length - MAX_DIAGNOSTIC_EVENTS);
        } catch (_e) {}
      };
      pc.onicegatheringstatechange = () => {
        console.log('[WebRTC] 🔍 ICE Gathering State:', pc.iceGatheringState);
        setPcIceGatheringState(pc.iceGatheringState);
        try {
          const a = diagnosticsRef.current.events;
          a.push({ ts: Date.now(), type: 'iceGatheringState', detail: pc.iceGatheringState });
          if (a.length > MAX_DIAGNOSTIC_EVENTS) a.splice(0, a.length - MAX_DIAGNOSTIC_EVENTS);
        } catch (_e) {}
      };
      pc.onconnectionstatechange = () => {
        if (pcRef.current !== pc) return; // stale callback — PC was replaced
        const state = pc.connectionState;
        console.log('[WebRTC] 🔌 Connection State:', state);
        setPcConnectionState(state);
        try {
          const a = diagnosticsRef.current.events;
          a.push({ ts: Date.now(), type: 'connectionState', detail: state });
          if (a.length > MAX_DIAGNOSTIC_EVENTS) a.splice(0, a.length - MAX_DIAGNOSTIC_EVENTS);
        } catch (_e) {}
        if (state === 'connected' && mountedRef.current) {
          setStatus('connected');
          // Successful reconnect — clear the burst counter so future failures
          // get a fresh budget. Also clear any "give up" banner.
          reconnectAttemptsRef.current = 0;
          setReconnectAttempt(0);
          quickRetriesRef.current = 0;
          if (mountedRef.current) setReconnectFailed(false);
        }
        if ((state === 'failed' || state === 'closed') && mountedRef.current) {
          console.error('[WebRTC] ❌ Connection State FAILED:', state);
          setStatus('error');
          setError('Conexión WebRTC cerrada');
          // Attempt reconnect unless user requested stop
          if (!userStoppedRef.current) {
            const attempts = reconnectAttemptsRef.current || 0;
            const delay = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
            try {
              // cleanup current pc before reconnect
              cleanup();
            } catch {}
            scheduleReconnect(delay, `pc-${state}`);
          }
        }
      };

      // Manejar data channel creado por el servidor remoto
      pc.ondatachannel = (event) => {
        try {
          console.log(
            '[DataChannel] ondatachannel received, label=',
            event.channel.label,
            'readyState=',
            event.channel.readyState
          );
        } catch (_e) {}
        setupDataChannel(event.channel);
      };

      pc.onicecandidate = (ev) => {
        try {
          if (ev.candidate && pcRef.current === pc) {
            // setLocalDescription can produce candidates before the offer is sent.
            // Preserve them until the backend has created this session.
            if (offerSent) sendCandidate(ev.candidate);
            else earlyCandidates.push(ev.candidate);
          }
        } catch {}
        try {
          const a = diagnosticsRef.current.events;
          const detail =
            ev && ev.candidate
              ? { sdpMid: ev.candidate.sdpMid, sdpMLineIndex: ev.candidate.sdpMLineIndex }
              : { candidate: null };
          a.push({ ts: Date.now(), type: 'iceCandidate', detail });
          if (a.length > MAX_DIAGNOSTIC_EVENTS) a.splice(0, a.length - MAX_DIAGNOSTIC_EVENTS);
        } catch (_e) {}
      };

      // Crear data channel para input/control (solo en modo gui)
      if (mode === 'gui') {
        try {
          const inputChannel = pc.createDataChannel('input', { ordered: true });
          try {
            console.log(
              '[DataChannel] created local data channel, label=',
              inputChannel.label,
              'readyState=',
              inputChannel.readyState
            );
          } catch (_e) {}
          setupDataChannel(inputChannel);
        } catch (err) {
          console.warn('Error creando input data channel:', err);
        }
      }

      if (mode === 'gui') {
        try {
          for (const _display of selected) pc.addTransceiver('video', { direction: 'recvonly' });
        } catch {}
      }

      // Use shared AdminWSManager for signaling (reuse admin WS connection)
      const mgr = AdminWSManager.get();

      // Check if admin WS is connected BEFORE trying to send offer
      const wsReady = mgr.getReadyState?.() === WebSocket.OPEN;
      console.log('[WebRTC] 🌐 Admin WebSocket ready:', wsReady);
      if (!wsReady) {
        console.warn('[WebRTC] ⚠️ Admin WebSocket no conectado, iniciándolo...');
        mgr.start?.();
        // Wait up to 5s for the admin WS to actually open (the previous 500ms
        // hardcoded wait was too short on slow networks and on first connect;
        // it caused silent send failures that the OFFER_TIMEOUT was left to
        // catch as a generic "agent not responding" error). We poll the
        // readyState so we react as soon as it transitions, instead of always
        // waiting the full budget.
        const WS_OPEN_TIMEOUT_MS = 5000;
        const WS_OPEN_POLL_MS = 50;
        const wsOpenDeadline = Date.now() + WS_OPEN_TIMEOUT_MS;
        while (Date.now() < wsOpenDeadline) {
          if (mgr.getReadyState?.() === WebSocket.OPEN) break;
          await new Promise((resolve) => setTimeout(resolve, WS_OPEN_POLL_MS));
        }
        if (mgr.getReadyState?.() !== WebSocket.OPEN) {
          console.warn(
            '[WebRTC] ⚠️ Admin WebSocket no abrió en %dms — el offer puede fallar silenciosamente',
            WS_OPEN_TIMEOUT_MS
          );
        }
      }

      const onMsg = (message: any) => {
        try {
          const type = message?.type as string;
          if (type && type.startsWith('webrtc')) {
            handleSignalMessage(message as WebRTCSignalMessage);
          }
          if (type === 'session.state') {
            const data = message?.data as MultiUserSessionState | undefined;
            if (data && data.agentId === agentId) {
              setSessionState(data);
              const mySessionId = sessionIdRef.current;
              if (mySessionId) {
                const myRole = data.controllerSessionId === mySessionId ? 'controller' : 'observer';
                roleRef.current = myRole;
                setRole(myRole);
              }
            }
          }
        } catch {}
      };
      mgr.addMessageListener?.(onMsg);

      // Send offer via admin WS with timeout
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        console.log('[WebRTC] 📤 Enviando oferta al agente, session:', sessionIdRef.current);

        const offerData: Record<string, unknown> = {
          agentId,
          sessionId: sessionIdRef.current,
          sdp: offer.sdp,
          mode,
          displayIndex,
          displays: selected,
        };
        if (connectPasswordRef.current) {
          offerData.password = connectPasswordRef.current;
        }
        const ok = mgr.send?.({ type: 'webrtc.offer', data: offerData });
        if (!ok) {
          throw new Error('Failed to send offer: Admin WS not connected');
        }
        offerSent = true;
        for (const candidate of earlyCandidates.splice(0)) sendCandidate(candidate);
        console.log('[WebRTC] ✅ Oferta enviada correctamente');

        // Set timeout waiting for answer - if no answer within OFFER_TIMEOUT_MS, assume failure
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
        }
        console.log('[WebRTC] ⏳ Esperando respuesta del agente (timeout: 15s)...');
        connectionTimeoutRef.current = window.setTimeout(() => {
          connectionTimeoutRef.current = null;
          if (mountedRef.current && pcRef.current?.connectionState !== 'connected') {
            console.error('[WebRTC] ❌ TIMEOUT: El agente no respondió en 15 segundos');
            setError('Agente no responde (timeout de señalización)');
            setStatus('error');
            mgr.removeMessageListener?.(onMsg);
            cleanup();
          }
        }, OFFER_TIMEOUT_MS) as unknown as number;
      } catch (err) {
        console.error('[WebRTC] ❌ Error creando/enviando oferta:', err);
        if (mountedRef.current) {
          setError(
            `No se pudo iniciar la sesión: ${err instanceof Error ? err.message : String(err)}`
          );
          setStatus('error');
        }
        mgr.removeMessageListener?.(onMsg);
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        return;
      }
      // store message listener removal for cleanup
      (wsRef as any).removeMsgListener = () => mgr.removeMessageListener?.(onMsg);
    },
    [
      agentId,
      attachStream,
      cleanup,
      handleSignalMessage,
      scheduleReconnect,
      setupDataChannel,
      setError,
      setPcConnectionState,
      setPcIceConnectionState,
      setPcIceGatheringState,
      setPcSignalingState,
      setReconnectAttempt,
      setReconnectFailed,
      setRole,
      setSessionState,
      setStatus,
      resetPcState,
    ]
  );

  // Actualizar ref de startSession para evitar referencias circulares
  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  // submitPassword reintenta la conexión con la contraseña provista (gate del agente).
  const submitPassword = useCallback(
    (password: string) => {
      connectPasswordRef.current = password;
      // Compartir con el terminal (mismo gate) durante esta pestaña.
      if (agentId) {
        try {
          sessionStorage.setItem(`ra_conn_pw_${agentId}`, password);
        } catch (_e) {
          /* ignore */
        }
      }
      setAuthRequired(false);
      setError(null);
      reconnectAttemptsRef.current = 0;
      setReconnectAttempt(0);
      startSession(modeRef.current, displayIndexRef.current);
    },
    [startSession, agentId, setAuthRequired, setError, setReconnectAttempt]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // cleanup() sends the explicit stop before it clears sessionIdRef.
      cleanup();
    };
  }, [agentId, cleanup]);

  // Ensure session is cleaned up on tab close / browser close.
  useEffect(() => {
    const handleUnload = () => {
      const sessionId = sessionIdRef.current;
      if (agentId && sessionId) {
        try {
          AdminWSManager.get().send?.({ type: 'webrtc.stop', data: { agentId, sessionId } });
        } catch (_e) {}
      }
    };
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [agentId]);

  const state = useMemo(
    () => ({ status, error, mode: modeRef.current, displayIndex: displayIndexRef.current }),
    [status, error]
  );

  const takeover = useCallback(() => {
    if (!agentId || !sessionIdRef.current) return;
    AdminWSManager.get().send?.({
      type: 'webrtc.takeover',
      data: { agentId, sessionId: sessionIdRef.current },
    });
  }, [agentId]);

  const lockSession = useCallback(() => {
    if (!agentId || !sessionIdRef.current) return;
    AdminWSManager.get().send?.({
      type: 'webrtc.lock',
      data: { agentId, sessionId: sessionIdRef.current },
    });
  }, [agentId]);

  const unlockSession = useCallback(() => {
    if (!agentId || !sessionIdRef.current) return;
    AdminWSManager.get().send?.({
      type: 'webrtc.unlock',
      data: { agentId, sessionId: sessionIdRef.current },
    });
  }, [agentId]);

  const hideCursor = useCallback(() => {
    if (roleRef.current === 'observer') return;
    sendInput({ type: 'cursor', action: 'hide' });
  }, [sendInput]);

  const showCursor = useCallback(() => {
    if (roleRef.current === 'observer') return;
    sendInput({ type: 'cursor', action: 'show' });
  }, [sendInput]);

  // Manual retry used by the "click to retry" banner shown after the reconnect
  // burst cap is hit. Resets counters, clears any pending reconnect, and
  // starts a fresh session synchronously.
  const retrySession = useCallback(() => {
    try {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    } catch {}
    reconnectAttemptsRef.current = 0;
    setReconnectAttempt(0);
    quickRetriesRef.current = 0;
    setReconnectFailed(false);
    return startSession(modeRef.current, displayIndexRef.current);
  }, [startSession, setReconnectAttempt, setReconnectFailed]);

  // Changing the number or identity of tracks requires a fresh SDP negotiation.
  const setDisplays = useCallback(
    (displays: number[]) => {
      const clean = [
        ...new Set((displays || []).filter((n) => Number.isInteger(n) && n >= 0)),
      ].slice(0, 16);
      const next = clean.length ? clean : [0];
      displaysRef.current = next;
      displayIndexRef.current = next[0] ?? 0;
      if (!sessionIdRef.current || (status !== 'connected' && status !== 'connecting')) return;
      return startSession(modeRef.current, next[0] ?? 0);
    },
    [status, startSession]
  );

  return {
    videoRef,
    // PR 3: per-track <video> ref registration. The page renders one
    // <video> per active monitor and registers it under its track
    // label (e.g. "screen-0"). The hook routes incoming MediaStreams
    // to the right element so the right pane shows the right feed.
    // The legacy `videoRef` is kept for tests + the single-monitor
    // fallback path; pages migrating to multi-track use only the
    // register/unregister pair.
    registerVideoRef,
    status,
    error,
    authRequired,
    submitPassword,
    state,
    role,
    sessionState,
    startSession,
    stopSession,
    setDisplays,
    sendInput,
    enqueueInput,
    takeover,
    lockSession,
    unlockSession,
    // Clipboard helpers
    startClipboardSync,
    stopClipboardSync,
    pushClipboardToAgent,
    pushClipboardFilesToAgent,
    pullClipboardFromAgent,
    requestRemoteClipboard,
    // Cursor control
    cursorHidden,
    hideCursor,
    showCursor,
    // Reconnect coordination (AC #2 — banner + retry after burst cap)
    reconnectFailed,
    reconnectAttempt,
    retrySession,
    // Bug 4: input error banner. Latest `input.error` from the agent,
    // plus a dismiss handler. Null when no recent error.
    inputError,
    dismissInputError,
    // PR 3: input session version. Bumps every time the input data
    // channel opens (initial connect and every reconnection). Consumers
    // can use it as a dependency in effects that need to re-sync
    // state after a reset (modifiers, cursor visibility, etc.).
    inputSessionVersion,
    // PR 5: per-session input traffic counters + last self-test RTT.
    // Surfaced in the DebugPanel so operators can spot a degraded
    // data channel before any user click fails.
    inputStats,
    // PR 6: live PeerConnection state. The page renders an inline
    // WebRTCStateBadge with these so an operator can diagnose a
    // "no video" symptom without opening DevTools — see the
    // WebRTCStateBadge component for how each state maps to a chip
    // color. (We destructure these from `sessionState_` at the top
    // of the hook — re-shorthand them here for the public return.)
    pcConnectionState,
    pcIceConnectionState,
    pcSignalingState,
    pcIceGatheringState,
    tracksReceived,
    lastTrackLabel,
    lastTrackKind,
  };
};
